import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

function friendlySignupError(err) {
  const code = err?.code || '';
  const message = err?.message || '';

  if (code === 'functions/resource-exhausted' || message.includes('resource-exhausted')) {
    return message.replace(/^.*?:\s*/, '') || 'Too many signup attempts. Please try again later.';
  }
  if (code === 'functions/already-exists') {
    return message.replace(/^.*?:\s*/, '') || 'This email already has an account. Please log in instead.';
  }
  if (code === 'functions/invalid-argument') {
    return message.replace(/^.*?:\s*/, '') || 'Please check the form fields and try again.';
  }
  // App Check missing/invalid typically surfaces as unauthenticated / failed-precondition / 401
  if (
    code === 'functions/unauthenticated' ||
    code === 'functions/failed-precondition' ||
    message.toLowerCase().includes('app check')
  ) {
    return 'Signup could not be verified (bot protection). Refresh the page and try again.';
  }
  return message || 'Failed to submit signup request. Please try again.';
}

/**
 * Submit a signup request via a Cloud Function.
 *
 * Requires Firebase App Check (reCAPTCHA v3) on the client. The function
 * enforces App Check, honeypot/timing checks, and IP/global rate limits.
 *
 * @param {object} data
 * @param {string} data.name
 * @param {string} data.email
 * @param {string} data.company
 * @param {string} data.role
 * @param {string|null} [data.website]
 * @param {string|null} [data.extraDetails]
 * @param {string} [data.companyUrl] honeypot — must be empty
 * @param {number} data.formOpenedAt Date.now() when the modal opened
 */
export async function submitSignupRequest(data) {
  if (!process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
    throw new Error(
      'Signup is unavailable: bot protection is not configured. Please contact the administrator.'
    );
  }
  try {
    const callable = httpsCallable(functions, 'submitSignupRequest');
    const result = await callable({
      name: data.name,
      email: data.email,
      company: data.company,
      role: data.role,
      website: data.website ?? null,
      extraDetails: data.extraDetails ?? null,
      companyUrl: data.companyUrl || '',
      formOpenedAt: data.formOpenedAt,
    });
    return result.data;
  } catch (err) {
    throw new Error(friendlySignupError(err));
  }
}
